import React from 'react';
import { cn } from '../../lib/cn';

interface RiskMatrixVisualizationProps {
  data: number[][];
  type: 'inherent' | 'residual';
}

// Same zone banding as the dashboard matrix (LedgerRiskMatrix).
function zoneClass(score: number): string {
  if (score <= 3) return 'bg-zone-1';
  if (score <= 6) return 'bg-zone-2';
  if (score <= 10) return 'bg-zone-3';
  if (score <= 16) return 'bg-zone-4';
  return 'bg-zone-5';
}

const LEGEND: Array<{ tone: string; label: string }> = [
  { tone: 'bg-zone-1', label: '1–3' },
  { tone: 'bg-zone-2', label: '4–6' },
  { tone: 'bg-zone-3', label: '7–10' },
  { tone: 'bg-zone-4', label: '11–16' },
  { tone: 'bg-zone-5', label: '17–25' },
];

// Rows are likelihood descending, columns impact ascending, matching the
// dashboard grid. These used to be the other way round, so the same data read
// mirrored between the two places somebody would compare it.
const likelihoodLabels = ['Very High', 'High', 'Medium', 'Low', 'Very Low'];
const impactLabels = ['Very Low', 'Low', 'Medium', 'High', 'Very High'];

const RiskMatrixVisualization: React.FC<RiskMatrixVisualizationProps> = ({ data, type }) => {
  // Calculate risk level for a cell
  const getRiskLevel = (row: number, col: number) => {
    const likelihood = 5 - row; // Convert row to likelihood (5 at top, 1 at bottom)
    const impact = col + 1; // Convert col to impact (1 at left, 5 at right)
    return impact * likelihood;
  };

  return (
    <figure aria-label={`${type} risk matrix`} className="mx-auto max-w-xl">
      <div className="flex gap-2">
        <div
          className="cap-label self-center !text-[9px]"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          Likelihood ↑
        </div>
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-[minmax(3.5rem,auto)_repeat(5,1fr)]">
            {/* Empty corner cell */}
            <div />

            {/* Impact labels (top) */}
            {impactLabels.map((label, index) => (
              <div
                key={`impact-${index}`}
                className="cap-label flex items-end justify-center pb-1 text-center !text-[9px]"
              >
                {label}
              </div>
            ))}

            {/* Likelihood labels and matrix cells */}
            {data.map((row, rowIndex) => (
              <React.Fragment key={`row-${rowIndex}`}>
                {/* Likelihood label */}
                <div className="cap-label flex items-center justify-end pr-2 text-right !text-[9px]">
                  {likelihoodLabels[rowIndex]}
                </div>

                {/* Risk cells */}
                {row.map((count, colIndex) => (
                  <div
                    key={`cell-${rowIndex}-${colIndex}`}
                    className={cn(
                      'flex aspect-[5/4] items-center justify-center border border-rule-faint font-mono text-scale-1 text-ink',
                      zoneClass(getRiskLevel(rowIndex, colIndex)),
                    )}
                  >
                    {count > 0 ? count : ''}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
          <div className="cap-label mt-1 text-center !text-[9px]">Impact →</div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1">
        {LEGEND.map((band) => (
          <span key={band.label} className="flex items-center gap-1">
            <span aria-hidden className={cn('h-3 w-3 border border-rule-faint', band.tone)} />
            <span className="font-mono text-scale-0 text-ink-muted">{band.label}</span>
          </span>
        ))}
      </div>
    </figure>
  );
};

export default RiskMatrixVisualization;
